export function getPropertyFilterI18nStrings() {
  return {
    filteringAriaLabel: 'Filter data',
    filteringPlaceholder: 'Filter by property or value',
    dismissAriaLabel: 'Dismiss',
    clearAriaLabel: 'Clear',
    groupValuesText: 'Values',
    groupPropertiesText: 'Properties',
    operatorsText: 'Operators',
    operationAndText: 'and',
    operationOrText: 'or',
    operatorLessText: 'Less than',
    operatorLessOrEqualText: 'Less than or equal',
    operatorGreaterText: 'Greater than',
    operatorGreaterOrEqualText: 'Greater than or equal',
    operatorContainsText: 'Contains',
    operatorDoesNotContainText: 'Does not contain',
    operatorEqualsText: 'Equals',
    operatorDoesNotEqualText: 'Does not equal',
    editTokenHeader: 'Edit filter',
    propertyText: 'Property',
    operatorText: 'Operator',
    valueText: 'Value',
    cancelActionText: 'Cancel',
    applyActionText: 'Apply',
    allPropertiesLabel: 'All properties',
    tokenLimitShowMore: 'Show more',
    tokenLimitShowFewer: 'Show fewer',
    clearFiltersText: 'Clear filters',
    removeTokenButtonAriaLabel: (token) => `Remove token ${token.propertyKey} ${token.operator} ${token.value}`,
    enteredTextLabel: (text) => `Use: "${text}"`,
  };
}

// `placeholder` is deliberately omitted; each table supplies its own.
export const V2_PROPERTY_FILTERING_TEXT = {
  groupValuesText: 'Values',
  groupPropertiesText: 'Properties',
  operationAndText: 'and',
  operationOrText: 'or',
  operationNotAndText: 'and not',
  operationNotOrText: 'or not',
  clearFiltersText: 'Clear filter',
  allowFreeTextFiltering: true,
};

export function buildFilteringProperties(columnDefinitions) {
  if (!Array.isArray(columnDefinitions)) {
    return [];
  }
  return columnDefinitions
    .filter((col) => col && col.id)
    .map((col) => {
      const label = typeof col.header === 'string' ? col.header : col.id;
      return {
        key: col.id,
        propertyLabel: label,
        groupValuesLabel: `${label} values`,
        operators: ['=', '!=', ':', '!:'],
      };
    });
}

export function isNumericLike(value) {
  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }
  if (typeof value === 'string') {
    return value.trim() !== '' && !Number.isNaN(Number(value));
  }
  return false;
}

// All-integer columns are discrete ids/codes (e.g. account number) and stay
// enumerable; only a column with a fractional value counts as a measure.
export function isContinuousNumericMeasure(values) {
  if (!Array.isArray(values)) {
    return false;
  }
  const nonEmpty = values.filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
  if (nonEmpty.length === 0 || !nonEmpty.every(isNumericLike)) {
    return false;
  }
  return nonEmpty.some((v) => !Number.isInteger(Number(v)));
}

// Deliberately no cardinality cap: the property-filter dropdowns are type-ahead
// searchable, so long value lists stay usable. capFilterValues,
// buildFilteringOptions and getAsinMetricsFilteringOptions MUST all route through
// this predicate so they cannot diverge.
export function shouldEnumerateFilterValues(values) {
  if (!Array.isArray(values)) {
    return false;
  }
  return !isContinuousNumericMeasure(values);
}

export function capFilterValues(values) {
  if (!Array.isArray(values)) return [];
  return shouldEnumerateFilterValues(values) ? values : [];
}

export function buildFilteringOptions(rows, filteringProperties) {
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(filteringProperties)) {
    return [];
  }
  const options = [];
  filteringProperties.forEach((prop) => {
    const values = new Set();
    for (const row of rows) {
      const value = row ? row[prop.key] : undefined;
      if (value === undefined || value === null || value === '' || typeof value === 'object') {
        continue;
      }
      values.add(String(value));
    }
    const distinct = Array.from(values);
    if (distinct.length > 0 && shouldEnumerateFilterValues(distinct)) {
      distinct.forEach((value) => options.push({ propertyKey: prop.key, value }));
    }
  });
  return options;
}

// Port of AWS-UI v2's non-exported `defaultFilteringFunction`
// (property-filtering/utils.js); keep in lockstep on AWS-UI upgrades. Library
// quirks (loose ==, no special case for a negated first token) are intentional.
const v2PropertyFilteringOperationFn = (operation) =>
  operation === 'or' ? (a, b) => a || b : (a, b) => a && b;

const v2PropertyFilteringPartialMatch = (token, item) => {
  const searchText = token.value.toLowerCase();
  const searchableProps = token.propertyKey ? [token.propertyKey] : Object.keys(item);
  return searchableProps.some(
    (prop) => typeof item[prop] === 'string' && item[prop].toLowerCase().indexOf(searchText) !== -1
  );
};

// eslint-disable-next-line eqeqeq
const v2PropertyFilteringFullMatch = (token, item) => token.value == item[token.propertyKey];

// Loose like the full match: the library stringifies option values into tokens.
const v2PropertyFilteringArrayMatch = (arrayField) => (token, item) =>
  // eslint-disable-next-line eqeqeq
  Array.isArray(item[arrayField]) && item[arrayField].some((value) => value == token.value);

const v2PropertyFilteringMatchFn = (negated, strategy) =>
  negated ? (token, item) => !strategy(token, item) : strategy;

const v2PropertyFilteringStrategy = (token, arrayFieldByPropertyKey) => {
  if (token.isFreeText) {
    return v2PropertyFilteringPartialMatch;
  }
  const arrayField = arrayFieldByPropertyKey && arrayFieldByPropertyKey[token.propertyKey];
  return arrayField ? v2PropertyFilteringArrayMatch(arrayField) : v2PropertyFilteringFullMatch;
};

export const v2PropertyFilteringFunction = (item, tokens, operation, arrayFieldByPropertyKey) => {
  if (!tokens || !tokens.length) {
    return true;
  }
  return tokens.reduce((include, token) => {
    const opFn = v2PropertyFilteringOperationFn(operation);
    const strategy = v2PropertyFilteringStrategy(token, arrayFieldByPropertyKey);
    const eqFn = v2PropertyFilteringMatchFn(token.negated, strategy);
    return opFn(include, eqFn(token, item));
  }, operation !== 'or');
};

export const getFilteredItems = (items, tokens, operation, arrayFieldByPropertyKey) =>
  (items || []).filter((item) => v2PropertyFilteringFunction(item, tokens, operation, arrayFieldByPropertyKey));



// WEBPACK FOOTER //
// ./src/components/common/property_filter_helpers.js